import { useEffect, useMemo, useState } from "react";
import bridge from "@vkontakte/vk-bridge";
import {
  AdaptivityProvider,
  AppRoot,
  Button,
  Checkbox,
  ConfigProvider,
  Div,
  Group,
  ModalPage,
  ModalPageHeader,
  ModalRoot,
  Panel,
  PanelHeader,
  Placeholder,
  SplitCol,
  SplitLayout,
  Spinner
} from "@vkontakte/vkui";
import type { AuthUser } from "@kass/shared";
import { api, errorMessage } from "../api/client";
import type { MeResponse } from "../api/types";
import { useHashRoute } from "../routes/useHashRoute";
import { useAuthStore } from "./authStore";
import { DevUserSwitcher } from "../components/DevUserSwitcher";
import { HomePage } from "../pages/HomePage";
import { MembershipPage } from "../pages/MembershipPage";
import { PartnersPage, PartnerDetailPage } from "../pages/PartnersPage";
import { LeaderboardPage } from "../pages/LeaderboardPage";
import { ProfilePage } from "../pages/ProfilePage";
import {
  PartnerCheckPage,
  PartnerDashboardPage,
  PartnerDiscountsPage,
  PartnerOffersPage,
  PartnerStorePage
} from "../pages/PartnerCabinetPages";
import { AdminAuditPage, AdminHomePage, AdminMembersPage, AdminPartnersPage, AdminRolesPage } from "../pages/AdminPages";
import { LegalPage } from "../pages/LegalPages";

type AuthResponse = {
  token: string;
  user: AuthUser;
};

function pickVkLaunchParams(raw: string) {
  const clean = raw.replace(/^[?#]/, "");
  const query = clean.includes("?") ? clean.slice(clean.indexOf("?") + 1) : clean;
  const params = new URLSearchParams(query);

  if (!params.has("sign") || !params.has("vk_user_id")) {
    return undefined;
  }

  const launchParams = new URLSearchParams();
  params.forEach((value, key) => {
    if (key === "sign" || key.startsWith("vk_")) {
      launchParams.append(key, value);
    }
  });

  return launchParams.toString();
}

function getVkLaunchParams() {
  return pickVkLaunchParams(window.location.search) || pickVkLaunchParams(window.location.hash);
}

async function withTimeout<T>(promise: Promise<T>, timeoutMs = 1500): Promise<T | undefined> {
  return Promise.race([
    promise.catch(() => undefined),
    new Promise<undefined>((resolve) => window.setTimeout(() => resolve(undefined), timeoutMs))
  ]);
}

export function App() {
  const { path, navigate } = useHashRoute();
  const { token, user, selectedPartnerId, setSession, setUser, setPartners } = useAuthStore();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [booting, setBooting] = useState(true);
  const [authError, setAuthError] = useState("");
  const [devVkUserId, setDevVkUserId] = useState(import.meta.env.VITE_DEV_VK_USER_ID || "1001");
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const isDevAuthEnabled = import.meta.env.DEV || import.meta.env.VITE_DEV_AUTH_ENABLED === "true";

  const reloadMe = async () => {
    const response = await api.get<MeResponse>("/me");
    setMe(response.data);
    setUser(response.data.user);
    setPartners(response.data.partners);
  };

  const authenticate = async (nextDevVkUserId = devVkUserId) => {
    setBooting(true);
    setAuthError("");
    try {
      await withTimeout(bridge.send("VKWebAppInit"), 1000);
      void import("@vkontakte/vk-mini-apps-router");

      const launchParams = getVkLaunchParams();
      if (!launchParams && !isDevAuthEnabled) {
        const meResponse = await api.get<MeResponse>("/me");
        setMe(meResponse.data);
        setUser(meResponse.data.user);
        setPartners(meResponse.data.partners);
        return;
      }

      const userInfo = await withTimeout(bridge.send("VKWebAppGetUserInfo"), 1000);
      const raw = userInfo as { first_name?: string; last_name?: string; photo_200?: string } | undefined;
      const profile = raw
        ? {
            firstName: raw.first_name,
            lastName: raw.last_name,
            avatarUrl: raw.photo_200
          }
        : undefined;

      const response = await api.post<AuthResponse>("/auth/vk", {
        launchParams,
        devVkUserId: isDevAuthEnabled ? nextDevVkUserId : undefined,
        profile
      });

      setSession(response.data.token, response.data.user);
      const meResponse = await api.get<MeResponse>("/me");
      setMe(meResponse.data);
      setUser(meResponse.data.user);
      setPartners(meResponse.data.partners);
    } catch (err) {
      setAuthError(errorMessage(err));
    } finally {
      setBooting(false);
    }
  };

  useEffect(() => {
    void authenticate(devVkUserId);
  }, []);

  useEffect(() => {
    if (token && user && !user.hasRequiredConsents) {
      setActiveModal("consent");
    }
  }, [token, user]);

  const page = useMemo(() => {
    const partnerMatch = path.match(/^\/partners\/([^/]+)$/);

    if (partnerMatch) {
      return <PartnerDetailPage id={partnerMatch[1]} navigate={navigate} />;
    }

    if (path.startsWith("/legal/")) {
      return <LegalPage path={path} navigate={navigate} />;
    }

    switch (path) {
      case "/membership":
        return <MembershipPage navigate={navigate} />;
      case "/partners":
        return <PartnersPage navigate={navigate} />;
      case "/leaderboard":
        return <LeaderboardPage />;
      case "/profile":
        return <ProfilePage me={me} reloadMe={reloadMe} />;
      case "/partner":
        return <PartnerDashboardPage key={selectedPartnerId ?? "partner"} navigate={navigate} />;
      case "/partner/check":
        return <PartnerCheckPage key={selectedPartnerId ?? "partner"} />;
      case "/partner/store":
        return <PartnerStorePage key={selectedPartnerId ?? "partner"} />;
      case "/partner/discounts":
        return <PartnerDiscountsPage key={selectedPartnerId ?? "partner"} />;
      case "/partner/offers":
        return <PartnerOffersPage key={selectedPartnerId ?? "partner"} />;
      case "/admin":
        return <AdminHomePage navigate={navigate} />;
      case "/admin/members":
        return <AdminMembersPage />;
      case "/admin/partners":
        return <AdminPartnersPage />;
      case "/admin/roles":
        return <AdminRolesPage />;
      case "/admin/audit":
        return <AdminAuditPage />;
      default:
        return <HomePage me={me} navigate={navigate} />;
    }
  }, [path, me, navigate, selectedPartnerId]);

  const modal = (
    <ModalRoot activeModal={activeModal} onClose={() => setActiveModal(null)}>
      <ConsentModal
        id="consent"
        onAccept={async () => {
          await api.patch("/profile/privacy", {
            personalDataAccepted: true,
            partnerNameCheckAccepted: true
          });
          await reloadMe();
          setActiveModal(null);
        }}
        onLegal={(nextPath) => {
          setActiveModal(null);
          navigate(nextPath);
        }}
      />
    </ModalRoot>
  );

  return (
    <ConfigProvider>
      <AdaptivityProvider>
        <AppRoot>
          <SplitLayout modal={modal}>
            <SplitCol>
              {booting ? (
                <Panel id="main">
                  <PanelHeader>Клубная карта</PanelHeader>
                  <Placeholder icon={<Spinner size="large" />}>Авторизация через VK</Placeholder>
                </Panel>
              ) : authError ? (
                <Panel id="main">
                  <PanelHeader>Ошибка входа</PanelHeader>
                  <Div className="page-content">
                    <Placeholder
                      header="Не удалось авторизоваться"
                      action={<Button onClick={() => authenticate(devVkUserId)}>Повторить</Button>}
                    >
                      {authError}
                    </Placeholder>
                    {isDevAuthEnabled ? (
                      <DevUserSwitcher
                        value={devVkUserId}
                        onChange={(vkUserId) => {
                          setDevVkUserId(vkUserId);
                          void authenticate(vkUserId);
                        }}
                      />
                    ) : null}
                  </Div>
                </Panel>
              ) : (
                page
              )}
              {!booting && !authError && isDevAuthEnabled ? (
                <Div className="page-content">
                  <DevUserSwitcher
                    value={devVkUserId}
                    onChange={(vkUserId) => {
                      setDevVkUserId(vkUserId);
                      void authenticate(vkUserId);
                    }}
                  />
                </Div>
              ) : null}
            </SplitCol>
          </SplitLayout>
        </AppRoot>
      </AdaptivityProvider>
    </ConfigProvider>
  );
}

function ConsentModal({
  id,
  onAccept,
  onLegal
}: {
  id: string;
  onAccept: () => Promise<void>;
  onLegal: (path: string) => void;
}) {
  const [personalData, setPersonalData] = useState(false);
  const [partnerNameCheck, setPartnerNameCheck] = useState(false);
  const [saving, setSaving] = useState(false);

  const canSubmit = personalData && partnerNameCheck;

  return (
    <ModalPage id={id} header={<ModalPageHeader>Согласия</ModalPageHeader>}>
      <Group>
        <Checkbox checked={personalData} onChange={(event) => setPersonalData(event.target.checked)}>
          Согласие на обработку персональных данных
        </Checkbox>
        <Checkbox checked={partnerNameCheck} onChange={(event) => setPartnerNameCheck(event.target.checked)}>
          Согласие на показ ФИО партнёру при проверке карты
        </Checkbox>
        <div className="toolbar" style={{ padding: "12px" }}>
          <Button mode="tertiary" onClick={() => onLegal("/legal/privacy")}>
            Политика
          </Button>
          <Button mode="tertiary" onClick={() => onLegal("/legal/consent")}>
            Текст согласий
          </Button>
          <Button
            loading={saving}
            disabled={!canSubmit}
            onClick={async () => {
              setSaving(true);
              try {
                await onAccept();
              } finally {
                setSaving(false);
              }
            }}
          >
            Принять
          </Button>
        </div>
      </Group>
    </ModalPage>
  );
}
